import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { Helmet } from 'react-helmet-async';

import config from '@src/config';
import leadGurusLogo from '@src/assets/logo-full-dark.png';
import { toast } from '@src/components/core/toaster';
import {
  fetchProspectVerticals,
  fetchProspectEstimate,
  fetchProspectZipDemographics,
  submitProspect,
} from '@src/api/actions/prospects';
import ProjectionZipMap from './components/ProjectionZipMap';

const STEPS = ['ZIP Codes', 'Verticals & Budget', 'Projections', 'Contact Info'];

function parseZips(text) {
  const parseLine = (line) => {
    if (!line) return { zip: '', territory: '', vertical: '', market_set: '' };
    const hasTab = line.includes('\t');
    const hasPipe = line.includes('|');
    let parts = [];
    if (hasTab) {
      parts = line.split('\t').map((p) => p.trim());
    } else if (hasPipe) {
      parts = line.split('|').map((p) => p.trim());
    } else {
      parts = line.split(',').map((p) => p.trim());
    }
    let zip = parts[0] || '';
    let territory = parts[1] || '';
    let vertical = parts[2] || '';
    let marketSet = parts.slice(3).join(' ').trim();
    return { zip, territory, vertical, market_set: marketSet };
  };

  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parsed = parseLine(line);
      let normalizedZip = parsed.zip.trim();
      if (/^\d{4}$/.test(normalizedZip)) normalizedZip = `0${normalizedZip}`;
      return {
        zip: normalizedZip,
        territory: parsed.territory.trim(),
        vertical: parsed.vertical.trim(),
        market_set: parsed.market_set.trim(),
      };
    })
    .filter((row) => row.zip);
}

const fmt = (n) => {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
};
const fmtCurrency = (n) => {
  if (n == null) return '—';
  return `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};
const fmtPct = (n) => {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${(Number(n) * 100).toFixed(1)}%`;
};
const fmtDec = (n) => {
  if (n == null || !Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('en-US');
};

export default function GetStartedPage() {
  const [activeStep, setActiveStep] = useState(0);

  // Step 1: ZIPs
  const [zipText, setZipText] = useState('');
  const [zipRows, setZipRows] = useState([]);

  // Step 2: Verticals & Budget
  const [availableVerticals, setAvailableVerticals] = useState([]);
  const [selectedVerticals, setSelectedVerticals] = useState([]);
  const [verticalBudgets, setVerticalBudgets] = useState({});

  // Step 3: Projections
  const [projectionSummary, setProjectionSummary] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [isLoadingProjection, setIsLoadingProjection] = useState(false);

  // Step 4: Contact info
  const [contactForm, setContactForm] = useState({
    company_name: '',
    contact_name: '',
    contact_email: '',
    phone: '',
    website: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Load verticals on mount
  useEffect(() => {
    fetchProspectVerticals()
      .then((res) => {
        setAvailableVerticals(res?.data?.verticals || []);
      })
      .catch(() => {});
  }, []);

  // Parse zips when text changes
  useEffect(() => {
    const parsed = parseZips(zipText);
    setZipRows(parsed);
  }, [zipText]);

  const totalBudget = useMemo(
    () => selectedVerticals.reduce((sum, v) => sum + (Number(verticalBudgets[v]) || 0), 0),
    [verticalBudgets, selectedVerticals]
  );

  // Load projections when entering step 3
  const loadProjections = useCallback(async () => {
    if (!zipRows.length || !selectedVerticals.length || totalBudget <= 0) return;
    setIsLoadingProjection(true);
    try {
      const [estimateRes, demoRes] = await Promise.all([
        fetchProspectEstimate({
          budget: totalBudget,
          verticals: selectedVerticals,
          vertical_budgets: verticalBudgets,
          scenario: 'base',
          zips: zipRows.map((r) => r.zip),
        }),
        fetchProspectZipDemographics({
          zip_rows: zipRows,
          verticals: selectedVerticals,
        }),
      ]);
      setProjectionSummary(estimateRes?.data?.summary || null);
      setDemographics(demoRes?.data || null);
    } catch (err) {
      toast.error('Failed to load projections. Please try again.');
    } finally {
      setIsLoadingProjection(false);
    }
  }, [zipRows, selectedVerticals, verticalBudgets, totalBudget]);

  const handleNext = () => {
    if (activeStep === 1) {
      // Going to step 3 — load projections
      setActiveStep(2);
      loadProjections();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleToggleVertical = (v) => {
    setSelectedVerticals((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    );
    if (!verticalBudgets[v]) {
      setVerticalBudgets((prev) => ({ ...prev, [v]: '' }));
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async () => {
    if (!contactForm.company_name || !contactForm.contact_name || !contactForm.contact_email) {
      toast.error('Please fill in all required fields.');
      return;
    }
    if (!isValidEmail(contactForm.contact_email)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setIsSubmitting(true);
    try {
      await submitProspect({
        ...contactForm,
        verticals: selectedVerticals,
        zip_data: zipRows,
        budget: totalBudget,
        vertical_budgets: verticalBudgets,
        projection_snapshot: projectionSummary,
        demographics_snapshot: demographics ? { territories: demographics.territories, totals: demographics.totals } : null,
        zip_rows_snapshot: zipRows,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = useMemo(() => {
    switch (activeStep) {
      case 0:
        return zipRows.length > 0;
      case 1:
        return selectedVerticals.length > 0 && totalBudget > 0;
      case 2:
        return !!projectionSummary;
      case 3:
        return contactForm.company_name && contactForm.contact_name && isValidEmail(contactForm.contact_email);
      default:
        return false;
    }
  }, [activeStep, zipRows, selectedVerticals, totalBudget, projectionSummary, contactForm]);

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Helmet><title>Thank You | {config.SITE.title}</title></Helmet>
        <Card sx={{ maxWidth: 520, mx: 'auto', textAlign: 'center', p: 4 }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>Thank You!</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            We've received your information and our team will review your projections.
            A member of the Lead Gurus team will reach out to {contactForm.contact_email} shortly.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {contactForm.company_name} &middot; {selectedVerticals.join(', ')} &middot; {zipRows.length} ZIP codes
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet><title>Get Started | {config.SITE.title}</title></Helmet>

      {/* Header */}
      <Box sx={{ bgcolor: '#111827', py: 2, px: 3 }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <img src={leadGurusLogo} alt="Lead Gurus" style={{ height: 28 }} />
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Title */}
        <Typography variant="h4" fontWeight={700} gutterBottom>
          See Your Lead Projections
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          Upload your ZIP codes, select your verticals and budget, and instantly see projected lead volumes and revenue.
        </Typography>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            {activeStep === 0 && (
              <StepZips zipText={zipText} setZipText={setZipText} zipRows={zipRows} />
            )}
            {activeStep === 1 && (
              <StepVerticals
                available={availableVerticals}
                selected={selectedVerticals}
                onToggle={handleToggleVertical}
                budgets={verticalBudgets}
                onBudgetChange={(v, val) => setVerticalBudgets((prev) => ({ ...prev, [v]: val }))}
                totalBudget={totalBudget}
              />
            )}
            {activeStep === 2 && (
              <StepProjections
                summary={projectionSummary}
                demographics={demographics}
                isLoading={isLoadingProjection}
                onRefresh={loadProjections}
              />
            )}
            {activeStep === 3 && (
              <StepContact form={contactForm} onChange={setContactForm} />
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <Stack direction="row" justifyContent="space-between">
          <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined">
            Back
          </Button>
          {activeStep < STEPS.length - 1 ? (
            <Button onClick={handleNext} variant="contained" disabled={!canProceed}>
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={!canProceed || isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={18} /> : <RocketLaunchIcon />}
            >
              {isSubmitting ? 'Submitting...' : 'Submit & Get Started'}
            </Button>
          )}
        </Stack>
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step 1: ZIP Codes
// ---------------------------------------------------------------------------
function StepZips({ zipText, setZipText, zipRows }) {
  return (
    <Stack spacing={3}>
      <Typography variant="h6">Upload Your ZIP Codes</Typography>
      <Typography variant="body2" color="text.secondary">
        Paste your ZIP codes below, one per line. You can optionally include territory and vertical
        separated by tabs, pipes, or commas (e.g., <code>60601 | Downtown | Roofing</code>).
      </Typography>
      <Alert severity="info" variant="outlined">
        <strong>Tip:</strong> The more ZIP codes you include, the better your campaign performance and cost of marketing will be. Do not send a filtered list — include all ZIP codes you can potentially service.
      </Alert>
      <TextField
        multiline
        minRows={8}
        maxRows={20}
        placeholder={`60601\n60614\n60657\n\nOr with territories:\n60601 | Downtown | Roofing\n60614 | North Side | Solar`}
        value={zipText}
        onChange={(e) => setZipText(e.target.value)}
        fullWidth
        variant="outlined"
        InputProps={{
          startAdornment: zipRows.length > 0 ? (
            <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
              <UploadFileIcon color="action" />
            </InputAdornment>
          ) : null,
        }}
      />
      {zipRows.length > 0 && (
        <Alert severity="success" variant="outlined">
          {zipRows.length} ZIP code{zipRows.length !== 1 ? 's' : ''} parsed successfully
        </Alert>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Verticals & Budget
// ---------------------------------------------------------------------------
function StepVerticals({ available, selected, onToggle, budgets, onBudgetChange, totalBudget }) {
  return (
    <Stack spacing={3}>
      <Typography variant="h6">Select Verticals & Set Budget</Typography>
      <Typography variant="body2" color="text.secondary">
        Choose the service verticals you offer and set a monthly budget for each.
      </Typography>
      <Alert severity="warning" variant="outlined">
        Our minimum total budget for a branded leads campaign is <strong>$15,000/month</strong> with a minimum of <strong>$10,000/month per vertical</strong>.
      </Alert>

      {/* Vertical chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {available.map((v) => (
          <Chip
            key={v}
            label={v}
            onClick={() => onToggle(v)}
            color={selected.includes(v) ? 'primary' : 'default'}
            variant={selected.includes(v) ? 'filled' : 'outlined'}
            sx={{ fontSize: 14, py: 2, px: 1 }}
          />
        ))}
        {available.length === 0 && (
          <Typography variant="body2" color="text.secondary">Loading verticals...</Typography>
        )}
      </Box>

      {/* Budget inputs */}
      {selected.length > 0 && (
        <>
          <Divider />
          <Typography variant="subtitle2">Monthly Budget per Vertical</Typography>
          <Grid container spacing={2}>
            {selected.map((v) => (
              <Grid item xs={12} sm={6} key={v}>
                <TextField
                  label={v}
                  type="number"
                  value={budgets[v] || ''}
                  onChange={(e) => onBudgetChange(v, e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
            ))}
          </Grid>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Total Monthly Budget</Typography>
            <Typography variant="h5" fontWeight={700}>{fmtCurrency(totalBudget)}</Typography>
          </Paper>
        </>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Projections
// ---------------------------------------------------------------------------
function StepProjections({ summary, demographics, isLoading, onRefresh }) {
  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography color="text.secondary">Calculating your projections...</Typography>
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography color="text.secondary">No projection data available.</Typography>
        <Button onClick={onRefresh} sx={{ mt: 2 }}>Retry</Button>
      </Box>
    );
  }

  const features = demographics?.features || [];
  const territories = demographics?.territories || [];
  const totals = demographics?.totals || {};

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Your Projections</Typography>

      {totals.population != null && totals.population < 1000000 && (
        <Alert severity="warning" variant="outlined">
          Your total serviceable population is <strong>{fmt(totals.population)}</strong>, which is under 1 million. Performance can vary with smaller populations. If you have more serviceable ZIP codes, adding them will improve campaign performance.
        </Alert>
      )}

      <Alert severity="info" variant="outlined">
        These projections are estimates based on historical data and industry benchmarks. Actual results may vary.
      </Alert>

      {/* Summary KPI blocks */}
      {(() => {
        const a = summary.assumptions || {};
        const leads = Math.round(summary.estimated_leads || 0);
        const cpl = summary.assumed_cpl;
        const setAppts = summary.verticals?.reduce((s, v) => s + (v.set_appointments || 0), 0);
        const issues = summary.verticals?.reduce((s, v) => s + (v.issues || 0), 0);
        const demos = summary.verticals?.reduce((s, v) => s + (v.demos || 0), 0);
        const netSold = summary.verticals?.reduce((s, v) => s + (v.net_sold || 0), 0);
        const netRev = summary.estimated_revenue;
        const comPct = netRev ? ((summary.budget / netRev) * 100).toFixed(1) + '%' : '—';
        const blocks = [
          { label: 'Total Budget', value: fmtCurrency(summary.budget), sub: summary.budget ? `~${fmtCurrency(summary.budget / 30)}/day` : null },
          { label: 'Leads & CPL', value: `${fmt(leads)} / ${cpl ? fmtCurrency(cpl) : '—'}`, sub: 'Est. leads / Blended CPL' },
          { label: 'Set Appointments', value: fmtDec(setAppts), sub: a.set_rate ? `Set rate ${fmtPct(a.set_rate)}` : null },
          { label: 'Issues', value: fmtDec(issues), sub: a.issue_rate ? `Issue rate ${fmtPct(a.issue_rate)}` : null },
          { label: 'Demos', value: fmtDec(demos), sub: a.demo_rate ? `Demo rate ${fmtPct(a.demo_rate)}` : null },
          { label: 'Net Sold', value: fmtDec(netSold), sub: a.net_close_rate ? `Close rate ${fmtPct(a.net_close_rate)}` : null },
          { label: 'Avg Job Size', value: a.avg_job_size ? fmtCurrency(a.avg_job_size) : '—' },
          { label: 'Net Revenue', value: fmtCurrency(netRev) },
          { label: 'Cost of Marketing', value: comPct },
        ];
        return (
          <Grid container spacing={1.5}>
            {blocks.map((b) => (
              <Grid item xs={6} sm={4} md={4} key={b.label}>
                <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
                  <Typography color="text.secondary" variant="caption">{b.label}</Typography>
                  <Typography variant="h6" fontWeight={700}>{b.value ?? '—'}</Typography>
                  {b.sub && <Typography color="text.secondary" variant="caption">{b.sub}</Typography>}
                </Paper>
              </Grid>
            ))}
          </Grid>
        );
      })()}

      {/* Per-vertical breakdown */}
      {summary.verticals?.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Vertical</TableCell>
              <TableCell align="right">Budget</TableCell>
              <TableCell align="right">CPL</TableCell>
              <TableCell align="right">Leads</TableCell>
              <TableCell align="right">Set Appts</TableCell>
              <TableCell align="right">Net Sold</TableCell>
              <TableCell align="right">Net Revenue</TableCell>
              <TableCell align="right">CoM%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summary.verticals.map((v) => {
              const com = v.net_revenue ? ((v.budget / v.net_revenue) * 100).toFixed(1) + '%' : '—';
              return (
                <TableRow key={v.name}>
                  <TableCell>{v.name}</TableCell>
                  <TableCell align="right">{fmtCurrency(v.budget)}</TableCell>
                  <TableCell align="right">{fmtCurrency(v.scenario_cpl)}</TableCell>
                  <TableCell align="right">{fmtDec(v.leads)}</TableCell>
                  <TableCell align="right">{fmtDec(v.set_appointments)}</TableCell>
                  <TableCell align="right">{fmtDec(v.net_sold)}</TableCell>
                  <TableCell align="right">{fmtCurrency(v.net_revenue)}</TableCell>
                  <TableCell align="right">{com}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* ZIP Map */}
      {features.length > 0 && (
        <Box sx={{ height: 400, borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
          <ProjectionZipMap features={features} territories={territories} />
        </Box>
      )}

      {/* Territory summary */}
      {territories.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Territory Summary</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Territory</TableCell>
                <TableCell>Vertical</TableCell>
                <TableCell align="right">ZIPs</TableCell>
                <TableCell align="right">Population</TableCell>
                <TableCell align="right">Med. Income</TableCell>
                <TableCell align="right">Med. Home Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {territories.map((t, i) => (
                <TableRow key={i}>
                  <TableCell>{t.territory}</TableCell>
                  <TableCell>{t.vertical}</TableCell>
                  <TableCell align="right">{fmt(t.zip_count)}</TableCell>
                  <TableCell align="right">{fmt(t.population)}</TableCell>
                  <TableCell align="right">{fmtCurrency(t.median_income)}</TableCell>
                  <TableCell align="right">{fmtCurrency(t.median_home_value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <Alert severity="info" variant="outlined">
        These projections are estimates based on historical data and industry benchmarks.
        Actual results may vary.
      </Alert>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 4: Contact Info
// ---------------------------------------------------------------------------
function formatPhone(value) {
  const digits = (value || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function StepContact({ form, onChange }) {
  const update = (field) => (e) => onChange({ ...form, [field]: e.target.value });

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Your Contact Information</Typography>
      <Typography variant="body2" color="text.secondary">
        Tell us about yourself so our team can follow up with you about your projections.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Company Name"
            value={form.company_name}
            onChange={update('company_name')}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Your Name"
            value={form.contact_name}
            onChange={update('contact_name')}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Email"
            type="email"
            value={form.contact_email}
            onChange={update('contact_email')}
            fullWidth
            required
            error={!!form.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)}
            helperText={form.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email) ? 'Enter a valid email address' : ''}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Phone (optional)"
            placeholder="(555) 123-4567"
            value={form.phone}
            onChange={(e) => onChange({ ...form, phone: formatPhone(e.target.value) })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Company Website"
            placeholder="https://www.yourcompany.com"
            value={form.website}
            onChange={update('website')}
            fullWidth
            helperText="Used to pull your logo, brand colors, and privacy policy"
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
